import { Suspense, lazy, useEffect } from "react";
import { Outlet, createBrowserRouter } from "react-router-dom";

import { AppSessionBridge } from "@/components/App";
import { Loading, RouteDocumentTitle } from "@/components/UI";
import { ROUTES } from "@/constants";
import { useAuth } from "@/hooks";
import { AccountLayout } from "@/layouts/AccountLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { MainLayout } from "@/layouts/MainLayout";

import { AuthRoute } from "./AuthRoute";

const HomePage = lazy(() => import("@/pages/Home"));
const LoginPage = lazy(() => import("@/pages/Login"));
const RegisterPage = lazy(() => import("@/pages/Register"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPassword"));
const ProductsPage = lazy(() => import("@/pages/Products"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetail"));
const GoldPricePage = lazy(() => import("@/pages/GoldPrice"));
const CheckoutPage = lazy(() => import("@/pages/Checkout"));
const CheckoutTransferPage = lazy(() => import("@/pages/CheckoutTransfer"));
const CollectionPage = lazy(() => import("@/pages/Collection"));
const NewsPage = lazy(() => import("@/pages/News"));
const NewsDetailPage = lazy(() => import("@/pages/NewsDetail"));
const PolicyPage = lazy(() => import("@/pages/Policy"));
const ProfilePage = lazy(() => import("@/pages/Profile"));
const AddressesPage = lazy(() => import("@/pages/Addresses"));
const OrdersPage = lazy(() => import("@/pages/Orders"));
const AccountOrderDetailPage = lazy(() => import("@/pages/OrderDetail"));
const SecurityPage = lazy(() => import("@/pages/Security"));
const NotFoundPage = lazy(() => import("@/pages/NotFound"));
const SocialCallbackPage = lazy(() => import("@/pages/SocialCallback"));

const RouteSuspense = () => (
  <Suspense fallback={<Loading fullScreen tip="Đang dựng storefront..." />}>
    <Outlet />
  </Suspense>
);

const AppRouteBootstrap = () => {
  const { accessToken, checkAuth, user } = useAuth();

  useEffect(() => {
    if (!accessToken || user) {
      return;
    }

    void checkAuth();
  }, [accessToken, checkAuth, user]);

  return (
    <>
      <AppSessionBridge />
      <RouteDocumentTitle />
      <Outlet />
    </>
  );
};

export const appRouter = createBrowserRouter([
  {
    element: <AppRouteBootstrap />,
    children: [
      {
        element: <RouteSuspense />,
        children: [
          {
            element: <MainLayout />,
            children: [
              {
                path: ROUTES.HOME,
                element: <HomePage />,
                handle: { title: "Trang chủ" },
              },
              {
                path: ROUTES.GOLD_PRICE,
                element: <GoldPricePage />,
                handle: { title: "Giá vàng" },
              },
              {
                path: ROUTES.PRODUCTS,
                element: <ProductsPage />,
                handle: { title: "Sản phẩm" },
              },
              {
                path: ROUTES.PRODUCT_DETAIL,
                element: <ProductDetailPage />,
                handle: { title: "Chi tiết sản phẩm" },
              },
              {
                path: ROUTES.CHECKOUT,
                element: <CheckoutPage />,
                handle: { title: "Thanh toán" },
              },
              {
                path: ROUTES.CHECKOUT_TRANSFER,
                element: <CheckoutTransferPage />,
                handle: { title: "Chuyển khoản" },
              },
              {
                path: ROUTES.NEWS,
                element: <NewsPage />,
                handle: { title: "Tin tức" },
              },
              {
                path: ROUTES.NEWS_DETAIL,
                element: <NewsDetailPage />,
                handle: { title: "Chi tiết tin tức" },
              },
              {
                path: ROUTES.POLICY_DETAIL,
                element: <PolicyPage />,
                handle: { title: "Chính sách" },
              },
              {
                element: <AuthRoute />,
                children: [
                  {
                    element: <AccountLayout />,
                    children: [
                      {
                        path: ROUTES.ACCOUNT_PROFILE,
                        element: <ProfilePage />,
                        handle: { title: "Tài khoản" },
                      },
                      {
                        path: ROUTES.ACCOUNT_ADDRESSES,
                        element: <AddressesPage />,
                        handle: { title: "Sổ địa chỉ" },
                      },
                      {
                        path: ROUTES.ACCOUNT_ORDERS,
                        element: <OrdersPage />,
                        handle: { title: "Đơn hàng" },
                      },
                      {
                        path: ROUTES.ACCOUNT_ORDER_DETAIL,
                        element: <AccountOrderDetailPage />,
                        handle: { title: "Chi tiết đơn hàng" },
                      },
                      {
                        path: ROUTES.ACCOUNT_SECURITY,
                        element: <SecurityPage />,
                        handle: { title: "Bảo mật" },
                      },
                    ],
                  },
                ],
              },
              {
                path: ROUTES.COLLECTION_DETAIL,
                element: <CollectionPage />,
                handle: { title: "Bộ sưu tập" },
              },
              {
                path: "*",
                element: <NotFoundPage />,
                handle: { title: "Không tìm thấy" },
              },
            ],
          },
          {
            element: <AuthLayout />,
            children: [
              {
                path: ROUTES.LOGIN,
                element: <LoginPage />,
                handle: { title: "Đăng nhập" },
              },
              {
                path: ROUTES.FORGOT_PASSWORD,
                element: <ForgotPasswordPage />,
                handle: { title: "Quên mật khẩu" },
              },
              {
                path: ROUTES.REGISTER,
                element: <RegisterPage />,
                handle: { title: "Đăng ký" },
              },
              {
                path: ROUTES.SOCIAL_CALLBACK,
                element: <SocialCallbackPage />,
                handle: { title: "Đang đăng nhập" },
              },
            ],
          },
        ],
      },
    ],
  },
]);
